(ns minorodata.components.analytics-models.schema
  (:require ["@xyflow/react" :refer [Handle Position ReactFlowProvider]]
            [minorodata.analytics-models.domain :as domain]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [ui.dag :as ui.dag]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))


;; -----------------------------------------------------------------------------
;; Node renderers
;; -----------------------------------------------------------------------------

(defn- form-field-value
  "Live read of a form field's current value from re-posh."
  [form-id field-name]
  (let [form-state @(re-posh/subscribe [:form-model/pull [:form-model/id form-id]])]
    (some #(when (= (name field-name) (name (:form-field/name %)))
             (:form-field/value %))
          (:form-model/fields form-state))))


(defn- node-name-input
  "Editable title inside a DAG node. Value is read live from form-state so the
   input is truly controlled — typing in any other input bound to the same field
   reflects here immediately.

   Coerces form-id/field-name back to keywords because they lose their type going
   through React Flow's clj->js round-trip on node :data."
  [{:keys [form-id field-name placeholder]}]
  (let [form-id    (keyword form-id)
        field-name (keyword field-name)
        value      (form-field-value form-id field-name)]
    [:input
     {:value       (or value "")
      :placeholder placeholder
      :spell-check "false"
      :class       (clsx "nodrag w-full bg-transparent py-1"
                         "text-sm font-medium leading-4"
                         "border-b border-kleene-input-border hover:border-kleene-text/40 focus:border-kleene-secondary-01"
                         "focus:outline-none placeholder:text-kleene-text/40")
      :on-change   #(dispatch [:app.events/update-form-field
                               form-id field-name (-> % .-target .-value)])}]))


(defn- node-name-text
  "Static (read-only) title inside a DAG node."
  [value]
  [:div {:class "py-1 text-sm font-medium leading-4 truncate"} (or value "—")])


(defn- table-node-body [{:keys [data]}]
  (let [{:keys [field-name form-id optional? table-name read-only? display-name]} data]
    [ui.dag/node-group
     [ui.dag/node-root {:class "w-64"}
      [ui.dag/node-content
       [ui.dag/node-icon [tab-icon {:type "table" :size 24}]]
       [ui.dag/node-text {:class "grow"}
        (when table-name
          [typography/label {:class "block truncate text-kleene-text-secondary"} table-name])
        (if read-only?
          [node-name-text display-name]
          [node-name-input {:form-id     form-id
                            :field-name  field-name
                            :placeholder "table name"}])
        (when optional?
          [ui.dag/node-subtitle "optional"])]]]]))


(defn- model-node-body [{:keys [data]}]
  (let [{:keys [field-name form-id model-display-name read-only? instance-name]} data
        name (if read-only?
               instance-name
               (form-field-value (keyword form-id) (keyword field-name)))]
    [ui.dag/node-group
     [ui.dag/node-root {:class "w-64"}
      [ui.dag/node-content
       [ui.dag/node-icon [tab-icon {:type "model" :size 24}]]
       [ui.dag/node-text
        [:div {:class "text-sm font-medium leading-4 truncate"}
         (or name "Instance name")]
        [ui.dag/node-subtitle model-display-name]]]]]))


(defn- table-node [params]
  (reagent/as-element
   [:<>
    [:> Handle {:type "target" :position Position.Left :class "opacity-0"}]
    [table-node-body (js->clj params :keywordize-keys true)]
    [:> Handle {:type "source" :position Position.Right :class "opacity-0"}]]))


(defn- model-node [params]
  (reagent/as-element
   [:<>
    [:> Handle {:type "target" :position Position.Left :class "opacity-0"}]
    [model-node-body (js->clj params :keywordize-keys true)]
    [:> Handle {:type "source" :position Position.Right :class "opacity-0"}]]))


(def ^:private node-types
  #js {:table table-node
       :model model-node})


;; -----------------------------------------------------------------------------
;; Layout
;; -----------------------------------------------------------------------------

(def ^:private col-x-input  0)
(def ^:private col-x-model  320)
(def ^:private col-x-output 640)
(def ^:private row-step     100)


(defn- column-positions
  "Y-positions for a vertical column of `n` items, centered around 0."
  [n]
  (let [offset (* row-step 0.5 (dec n))]
    (for [i (range n)]
      (- (* i row-step) offset))))


(defn- table-field-name
  "Form-field key for a table's editable name."
  [direction table-name]
  (keyword (str (name direction) "-table:" table-name)))


(defn- table-node-spec
  [{:keys [direction col-x y form-id table read-only? display-name]}]
  (let [field-name (table-field-name direction (:table-name table))]
    {:id       (str (name direction) "-table:" (:table-name table))
     :type     "table"
     :position {:x col-x :y y}
     :data     (assoc table
                      :field-name field-name :form-id form-id
                      :read-only? read-only? :display-name display-name
                      ;; Only inputs can be optional; outputs are always required.
                      :optional? (and (= direction :input) (boolean (:can-be-empty table))))}))


(defn- build-graph [{:keys [model form-id read-only? input-names output-names model-name
                            input-mappings output-mappings]}]
  (let [{:keys [input-tables output-tables]} (:analytics-model-registry/table-definitions model)
        display-name (:analytics-model-registry/display-name model)
        in-ys        (column-positions (count input-tables))
        out-ys       (column-positions (count output-tables))
        in-names     (vec input-names)
        out-names    (vec output-names)
        ;; Read-only titles use the keyed per-direction mapping when populated,
        ;; else the positional vector. (No per-key fallback — it would misalign
        ;; dropped optional tables against the compacted vectors.)
        table-title  (fn [mappings names i t]
                       (if (seq mappings)
                         (get mappings (domain/mapping-key (:table-name t)))
                         (nth names i (:table-name t))))
        input-nodes  (map-indexed (fn [i t]
                                    (table-node-spec {:direction    :input
                                                      :col-x        col-x-input
                                                      :y            (nth in-ys i)
                                                      :form-id      form-id
                                                      :table        t
                                                      :read-only?   read-only?
                                                      :display-name (table-title input-mappings in-names i t)}))
                                  input-tables)
        output-nodes (map-indexed (fn [i t]
                                    (table-node-spec {:direction    :output
                                                      :col-x        col-x-output
                                                      :y            (nth out-ys i)
                                                      :form-id      form-id
                                                      :table        t
                                                      :read-only?   read-only?
                                                      :display-name (table-title output-mappings out-names i t)}))
                                  output-tables)
        model-node-data {:id       "model"
                         :type     "model"
                         :position {:x col-x-model :y 0}
                         :data     {:field-name         :name
                                    :form-id            form-id
                                    :model-display-name display-name
                                    :read-only?         read-only?
                                    :instance-name      model-name}}
        input-edges  (map (fn [t]
                            {:id     (str "edge:input:"  (:table-name t))
                             :source (str "input-table:" (:table-name t))
                             :target "model"})
                          input-tables)
        output-edges (map (fn [t]
                            {:id     (str "edge:output:"  (:table-name t))
                             :source "model"
                             :target (str "output-table:" (:table-name t))})
                          output-tables)]
    {:nodes (vec (concat input-nodes [model-node-data] output-nodes))
     :edges (vec (concat input-edges output-edges))}))


;; -----------------------------------------------------------------------------
;; Public component
;; -----------------------------------------------------------------------------

(defn schema-panel
  "Renders a model's I/O schema as a small left-to-right DAG:
   input tables → model → output tables.

   Edit mode (default): table titles and the instance name are editable inputs
   bound to the surrounding form's fields (via `:form-id`), read live so they
   stay in sync with any other input editing the same field.

   Read-only mode (`:read-only? true`): titles render as static text taken from
   the given `:instance` (input/output table names positionally, plus name).

   Props:
     :model       — the pulled analytics-model-registry entity
     :form-id     — form id for field updates/reads (edit mode)
     :read-only?  — render static titles instead of inputs
     :instance    — the analytics-model instance (read-only mode)"
  [{:keys [model instance read-only?] :as props}]
  (let [{:keys [input-tables output-tables]} (:analytics-model-registry/table-definitions model)]
    (when (or (seq input-tables) (seq output-tables))
      (let [model-params (:analytics-model/model-params instance)
            props      (cond-> props
                         read-only? (assoc :input-names      (:analytics-model/input-tables instance)
                                           :output-names     (:analytics-model/output-tables instance)
                                           :model-name       (:analytics-model/name instance)
                                           :input-mappings   (:input-tables model-params)
                                           :output-mappings  (:output-tables model-params)))
            {:keys [nodes edges]} (build-graph props)
            dark-mode?             @(re-posh/subscribe [:app/prop :app/dark-mode?])]
        [:div {:class "h-72 w-full border rounded-lg overflow-hidden relative"}
         [:> ReactFlowProvider
          [ui.dag/dag {:nodes      nodes
                       :edges      edges
                       :node-types node-types
                       :dark-mode? dark-mode?
                       :read-only? true
                       :zoom 1}
           [ui.dag/background {:dark? dark-mode?}]]]]))))
